import { useState, useMemo, useCallback, useRef } from 'react';

import { DateTime } from 'luxon';
import { parseDate } from 'async-timing';

import { generateTimeSlots, getValidInitialTime } from './generateTimeSlots';
import {
  SCHEDULE_OPTIONS,
  ScheduleOption,
  UseServiceRequestScheduleReturn,
} from './types';

const isValidDate = (date: Date | null): date is Date =>
  date !== null && !Number.isNaN(date.getTime());

export type UseServiceRequestScheduleOptions = {
  initialScheduledDueDate?: Date | string | null;
  timeZone?: string;
  onChange: (scheduledDueDate: Date | null | undefined) => void;
};

export function useServiceRequestSchedule({
  initialScheduledDueDate,
  timeZone,
  onChange,
}: UseServiceRequestScheduleOptions): UseServiceRequestScheduleReturn {
  const parsedDate = parseDate(initialScheduledDueDate);
  const isSpecificDate = parsedDate !== null;

  const initialScheduledDate = useRef(parsedDate).current;

  const [scheduleOption, setScheduleOption] = useState<ScheduleOption>(
    isSpecificDate ? SCHEDULE_OPTIONS.SCHEDULED_DATE : SCHEDULE_OPTIONS.ASAP
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(parsedDate);
  const [selectedTime, setSelectedTime] = useState<Date | null>(() =>
    getValidInitialTime(parsedDate, timeZone)
  );

  const isValid =
    scheduleOption === SCHEDULE_OPTIONS.ASAP || isValidDate(selectedTime);

  const preserveTime = useMemo(() => {
    if (!initialScheduledDate || !selectedDate) return null;

    const initialDt = timeZone
      ? DateTime.fromJSDate(initialScheduledDate).setZone(timeZone)
      : DateTime.fromJSDate(initialScheduledDate);
    const selectedDt = timeZone
      ? DateTime.fromJSDate(selectedDate).setZone(timeZone)
      : DateTime.fromJSDate(selectedDate);

    return initialDt.hasSame(selectedDt, 'day') ? initialScheduledDate : null;
  }, [initialScheduledDate, selectedDate, timeZone]);

  const timeSlots = useMemo(
    () =>
      selectedDate
        ? generateTimeSlots(selectedDate, timeZone, preserveTime)
        : [],
    [selectedDate, timeZone, preserveTime]
  );

  const minDate = useMemo(() => {
    const now = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();
    const todaySlots = generateTimeSlots(
      now.toJSDate(),
      timeZone,
      initialScheduledDate
    );

    return todaySlots.length === 0
      ? now.plus({ days: 1 }).startOf('day').toJSDate()
      : undefined;
  }, [timeZone, initialScheduledDate]);

  const handleScheduleOptionChange = useCallback(
    (option: ScheduleOption) => {
      if (
        option === SCHEDULE_OPTIONS.ASAP ||
        option === SCHEDULE_OPTIONS.SCHEDULED_DATE
      ) {
        setScheduleOption(option);

        if (option === SCHEDULE_OPTIONS.ASAP) {
          onChange(null);
          setSelectedDate(null);
          setSelectedTime(null);
        } else {
          onChange(undefined);
        }
      }
    },
    [onChange]
  );

  const handleDateChange = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      setSelectedTime(null);
      onChange(undefined);
    },
    [onChange]
  );

  const handleTimeChange = useCallback(
    (value: string) => {
      const date = new Date(value);

      setSelectedTime(date);
      onChange(date);
    },
    [onChange]
  );

  return {
    scheduleOption,
    selectedDate,
    selectedTime,
    timeSlots,
    minDate,
    isValid,
    handleScheduleOptionChange,
    handleDateChange,
    handleTimeChange,
  };
}
